import React, { useState, useEffect, useContext } from 'react';
import { Box, Button, Slide } from '@mui/material';
import Overview from './FormComp/Overview.js';
import Payment from './FormComp/Payment.js';
import Summary from './FormComp/Summary.js';
import ThankYou from './FormComp/ThankYou.js';
import PurchaseDAP from './FormComp/PurchaseDAP.js';
import './FormComp/styles/MultiPartForm.css';
import './FormComp/styles/FleetForm.css';
import './FormComp/styles/misc.css';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import AIOContext from '../AIOContext.js';
import Confirmation from './FormComp/Confirmation.js';
import formatCurrency from '../util/formatCurrency.js';
import apiURL from '../util/apiURL.js';
import ThankYouDAP from './FormComp/ThankYouDAP.js';
import UpdatePaymentMethod from './FormComp/UpdatePaymentMethod.js';
import { keyframes } from '@mui/system';
import { isMobile } from 'react-device-detect';

function ReservationForm({
  toggleFormVisibility,
  configureData,
  threejsModule,
  reservationData,
  setReservationData,
  formVisible,
  setOnMobileDialogMode,
}) {
  const stripe = useStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const elements = useElements();

  const { user, vehicle, dueAmount, dap, logo, fleet } = useContext(AIOContext);
  const { totalDapAmount } = dap;

  const [formMode, setFormMode] = useState('reservation');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState(false);
  const [paymentMethodError, setPaymentMethodError] = useState(false);
  const [confirmPaymentError, setConfirmPaymentError] = useState(false);
  const [reservationSubmitError, setReservationSubmitError] = useState(false);
  const [stripePaymentMethod, setStripePaymentMethod] = useState(null);

  useEffect(() => {
    if (userDetailsError) {
      setStep(1);
    }
  }, [userDetailsError]);

  useEffect(() => {
    if (reservationData) {
      setFormMode('confirmation');
    }
  }, []);

  const activatePurchaseDapForm = () => {
    setFormMode('purchaseDap');
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const stepForward = () => {
    if (step === 1) {
      if (checkUserDetails()) {
        setStep(2);
        setUserDetailsError(false);
      } else {
        setUserDetailsError(true);
      }
    }
  };

  const checkUserDetails = () => {
    if (
      user.userConfig.firstName &&
      user.userConfig.lastName &&
      isValidEmail(user.userConfig.emailAddress) &&
      (vehicle.vehicleConfig.isFleet ? user.userConfig.companyName : true)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleFinish = async () => {
    if (!checkUserDetails()) {
      setUserDetailsError(true);
      return;
    }

    setLoading(true);
    await postFormData();
  };

  const handleDapPurchase = async () => {
    setLoading(true);
    await postPurchaseDapData();
  };

  const handleNetworkFetchError = (error) => {
    console.log(error);
    setLoading(false);
    alert(
      'There was a network problem during your transaction. Please try again or contact an administrator.' +
        '\n\n' +
        error
    );
  };

  const handlePaymentMethodError = (error) => {
    console.log(error);
    setLoading(false);
    setPaymentMethodError(error);
  };

  const handleConfirmPaymentError = (error) => {
    console.log(error);
    setLoading(false);
    setConfirmPaymentError(error);
  };

  const handleReservationSubmitError = (error) => {
    console.log(error);
    setLoading(false);
    setReservationSubmitError(error);
  };

  const handleSubmitPaymentMethod = async () => {
    if (!stripe) {
      return;
    }
    setLoading(true);
    setPaymentMethodError(false);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log(submitError);
      handlePaymentMethodError(submitError.message);
      setLoading(false);
      return;
    }

    const { error, paymentMethod: stripePaymentMethod } =
      await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: `${user.userConfig.firstName} ${user.userConfig.lastName}`,
            email: user.userConfig.emailAddress,
          },
        },
      });

    console.log(error, stripePaymentMethod);
    if (error) {
      handlePaymentMethodError(error.message);
      setLoading(false);
      return;
    }

    user.setUserConfig((prevState) => ({
      ...prevState,
      cardBrand: stripePaymentMethod.card.brand,
      lastFour: stripePaymentMethod.card.last4,
      cardFunding: stripePaymentMethod.card.funding,
    }));
    setLoading(false);
    setStripePaymentMethod(stripePaymentMethod);
    setStep(3);
  };

  const handleUpdatePaymentMethod = async () => {
    if (!stripe) {
      return;
    }
    setLoading(true);
    setPaymentMethodError(false);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log(submitError);
      handlePaymentMethodError(submitError.message);
      setLoading(false);
      return;
    }

    const { error, paymentMethod: stripePaymentMethod } =
      await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: `${user.userConfig.firstName} ${user.userConfig.lastName}`,
            email: user.userConfig.emailAddress,
          },
        },
      });

    console.log(error, stripePaymentMethod);
    if (error) {
      handlePaymentMethodError(error.message);
      setLoading(false);
      return;
    }

    const updatePaymentMethodResponse = await fetch(
      `${apiURL}/api/payments/updatePaymentMethod`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: user.userConfig.customerId,
          payment_method_id: stripePaymentMethod.id,
        }),
      }
    )
      .then((res) => res.json())
      .catch((err) => handleNetworkFetchError(err));

    console.log(updatePaymentMethodResponse);

    if (!updatePaymentMethodResponse.success) {
      console.error(updatePaymentMethodResponse);
      handlePaymentMethodError(updatePaymentMethodResponse.error.raw.message);
      return;
    }

    user.setUserConfig((prevState) => ({
      ...prevState,
      cardBrand: stripePaymentMethod.card.brand,
      lastFour: stripePaymentMethod.card.last4,
      cardFunding: stripePaymentMethod.card.funding,
    }));

    setLoading(false);
    setStripePaymentMethod(stripePaymentMethod);
    setFormMode('purchaseDap');
  };

  const postFormData = async () => {
    const { vehicleQuantities } = vehicle.vehicleConfig;
    let targetVehicleType = vehicle.vehicleConfig.carType;
    if (fleet.isFleet) {
      // console.log('VEHICLE Q: ', vehicleQuantities);
      if (vehicleQuantities.van > 0) {
        targetVehicleType = 'van';
      } else if (vehicleQuantities.truck > 0) {
        targetVehicleType = 'truck';
      } else {
        targetVehicleType = 'car';
      }
      vehicle.vehicleConfig.carType = targetVehicleType;
    }
    const emailImage = isMobile
      ? ''
      : threejsModule.renderFeatureImage(targetVehicleType);
    // const emailImage = isMobile ? '' : threejsModule.renderFeatureImage();

    if (!stripePaymentMethod) return;

    const theDueAmount = window.location.search.includes('bftest')
      ? 100
      : dueAmount;

    const paymentIntentResponse = await fetch(
      `${apiURL}/api/payments/createPaymentIntent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subtotal: theDueAmount,
          // subtotal: 100,
          ...(!user.userConfig.customerId && {
            payment_method_id: stripePaymentMethod.id,
          }),
          ...(user.userConfig.customerId && {
            customer_id: user.userConfig.customerId,
          }),
        }),
      }
    )
      .then((res) => res.json())
      .catch((err) => handleNetworkFetchError(err));

    console.log(paymentIntentResponse);

    if (!paymentIntentResponse.success && !paymentIntentResponse.sucess) {
      console.error(paymentIntentResponse);
      handleConfirmPaymentError(paymentIntentResponse.error.raw.message);
      return;
    }

    user.setUserConfig((prevState) => ({
      ...prevState,
      ...(paymentIntentResponse.customer_id && {
        customerId: paymentIntentResponse.customer_id,
      }),
    }));

    const payload = {
      stripeCustomerId: paymentIntentResponse.customer_id,
      user: user.userConfig,
      vehicle: vehicle.vehicleConfig,
      customImage: emailImage,
      logoData: logo.logoData,
      fleet: fleet,
      dap: dap,
    };

    const reservationResponse = await fetch(
      `${apiURL}/api/reservations/standard`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .catch((err) => handleNetworkFetchError(err));

    console.log('RESPONSE', reservationResponse);

    if (reservationResponse.success) {
      const { confirmPaymentError } = await stripe.confirmPayment({
        clientSecret: paymentIntentResponse.client_secret,
        redirect: 'if_required',
      });

      if (confirmPaymentError) {
        console.log(confirmPaymentError);
        handleConfirmPaymentError(confirmPaymentError);
        return;
      }

      const data = reservationResponse.body[0];

      setReservationData({
        firstName: data.first_name,
        lastName: data.last_name,
        companyName: data.company_name,
        emailAddress: data.email_address,
        dapPurchased: data.dap === null ? false : data.dap,
        createdAt: data.created_at,

        cardBrand: data.card_brand,
        lastFour: data.card_last_four,
        cardFunding: data.card_funding,

        customerId: data.stripe_customer_id,
        uuid: data.aio_verse_url,
      });

      const url = new URL(window.location);
      url.searchParams.set('reservation', data.aio_verse_url);
      window.history.pushState({}, '', url);
      // add the reservation time to the user config
      user.setUserConfig((prevState) => ({
        ...prevState,
        createdAt: data.created_at,
        uuid: data.aio_verse_url,
      }));
      setLoading(false);
      setFormMode('thankYou');
    } else {
      console.error(reservationResponse);
      handleReservationSubmitError(reservationResponse.message);
    }

    return;
  };

  const postPurchaseDapData = async () => {
    const { vehicleQuantities } = vehicle.vehicleConfig;
    let targetVehicleType = vehicle.vehicleConfig.carType;
    if (fleet.isFleet) {
      // console.log('VEHICLE Q: ', vehicleQuantities);
      if (vehicleQuantities.van > 0) {
        targetVehicleType = 'van';
      } else if (vehicleQuantities.truck > 0) {
        targetVehicleType = 'truck';
      } else {
        targetVehicleType = 'car';
      }
      vehicle.vehicleConfig.carType = targetVehicleType;
    }
    const emailImage = isMobile
      ? ''
      : threejsModule.renderFeatureImage(targetVehicleType);

    const theDueAmount = window.location.search.includes('bftest')
      ? 100
      : totalDapAmount;

    const paymentIntentResponse = await fetch(
      `${apiURL}/api/payments/createPaymentIntent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subtotal: theDueAmount,
          // subtotal: 100,
          // ...(!user.userConfig.customerId && {
          //   payment_method_id: stripePaymentMethod.id,
          // }),
          // ...(user.userConfig.customerId && {
          //   customer_id: user.userConfig.customerId,
          // }),

          customer_id: user.userConfig.customerId,
        }),
      }
    )
      .then((res) => res.json())
      .catch((err) => handleNetworkFetchError(err));

    console.log(paymentIntentResponse);

    if (!paymentIntentResponse.success) {
      console.error(paymentIntentResponse);
      handleConfirmPaymentError(paymentIntentResponse.error.raw.message);
      return;
    }

    user.setUserConfig((prevState) => ({
      ...prevState,
      dapPurchased: true,
      dap: true,
    }));

    const payload = {
      aioVerseUrl: user.userConfig.uuid,
      user: user.userConfig,
      vehicle: vehicle.vehicleConfig,
      dap: {
        dapChecked: true,
        totalDapAmount: dap.totalDapAmount,
      },
      customImage: emailImage,
      logoData: logo.logoData,
      sendUpdate: true,
      fleet,
    };

    const updateReservationResponse = await fetch(
      `${apiURL}/api/reservations/standard`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .catch((err) => handleNetworkFetchError(err));

    console.log('RESPONSE', updateReservationResponse);

    if (updateReservationResponse.success) {
      const { confirmPaymentError } = await stripe.confirmPayment({
        clientSecret: paymentIntentResponse.client_secret,
        redirect: 'if_required',
      });

      if (confirmPaymentError) {
        console.log(confirmPaymentError);
        handleConfirmPaymentError(confirmPaymentError);
        return;
      }

      setLoading(false);
      setFormMode('thankYouDAP');
    } else {
      console.error(updateReservationResponse);
      handleReservationSubmitError(updateReservationResponse.message);
    }
  };

  const handleCloseForm = () => {
    toggleFormVisibility(true);
  };

  const contButtonStyles = `px-6 md:px-8 py-4 md:py-5 rounded-full flex justify-center items-center cursor-pointer transition-opacity bg-[#38B8AB] text-white font-bold font-wide text-xs md:text-sm gap-4 uppercase hover:opacity-80 transition-opacity`;

  const confirmButtonStyles = `px-6 md:px-8 py-4 md:py-5 rounded-full flex justify-center items-center cursor-pointer transition-opacity bg-[#38B8AB] text-white font-bold font-wide text-xs md:text-sm gap-4 uppercase hover:opacity-80 transition-opacity w-full`;

  const cancelButtonStyles = `px-6 md:px-8 py-4 md:py-5 rounded-full flex justify-center items-center cursor-pointer transition-opacity bg-white text-[#151515] font-bold font-wide text-xs md:text-sm border-[1px] border-[#151515] gap-4 uppercase hover:opacity-80 transition-opacity`;

  const spin = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(360deg);
}
`;

  return (
    <Box
      sx={{
        height: '100vh',
        position: 'fixed',
        right: 0,
        top: 0,
        transition: 'right 0.75s',
        zIndex: 100,
        borderRadius: '0 0 0 0',
      }}
      className='reservation-panel'
    >
      {loading && (
        <div className='absolute inset-0 z-50 bg-white bg-opacity-50 flex justify-center items-center'>
          <Box
            component='img'
            src='./assets/images/icons/LoadingCircle.svg'
            sx={{
              animation: `${spin} 0.6s linear infinite`,
              width: '224px',
            }}
          ></Box>
        </div>
      )}

      <button
        className={`absolute right-[calc(100%+24px)] top-10 w-14 h-14 rounded-full flex justify-center items-center cursor-pointer transition-opacity ${
          formVisible
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleCloseForm}
      >
        <svg
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clipPath='url(#clip0_1779_56952)'>
            <path
              d='M12.9989 8.00013C12.9989 8.4668 12.7989 8.8668 12.4656 9.13346L4.99893 15.7335C4.46559 16.2001 3.66559 16.1335 3.26559 15.6001C2.86559 15.0668 2.86559 14.3335 3.39893 13.8668L9.93226 8.13346C9.99893 8.0668 9.99893 8.00013 9.93226 7.8668L3.39893 2.13346C2.86559 1.6668 2.86559 0.866798 3.33226 0.400131C3.79893 -0.0665354 4.53226 -0.133202 5.06559 0.266798L12.5323 6.80013C12.7989 7.13346 12.9989 7.53346 12.9989 8.00013Z'
              fill='#151515'
            />
          </g>
          <defs>
            <clipPath id='clip0_1779_56952'>
              <rect width='16' height='16' fill='white' />
            </clipPath>
          </defs>
        </svg>
      </button>

      <Slide in={true} direction='left'>
        <div className='reservation-sidebar'>
          <Box className='scrollable' p={2}>
            <div className='flex md:hidden justify-end items-center pt-10 pb-10 pr-6 z-10 relative -mb-10'>
              <button
                className={`fixed t-10 r-6 w-10 h-10 rounded-full flex justify-center items-center cursor-pointer transition-opacity ${
                  formVisible
                    ? 'opacity-100 pointer-events-auto'
                    : 'opacity-0 pointer-events-none'
                }`}
                onClick={handleCloseForm}
              >
                <svg
                  width='8'
                  height='8'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <g clipPath='url(#clip0_1772_73836)'>
                    <path
                      d='M9.53133 8.11981C9.51539 8.10485 9.50268 8.08678 9.49399 8.06671C9.48531 8.04664 9.48083 8.02501 9.48083 8.00315C9.48083 7.98128 9.48531 7.95965 9.49399 7.93958C9.50268 7.91951 9.51539 7.90144 9.53133 7.88648L15.7047 1.70648C15.7982 1.61409 15.8724 1.50405 15.9231 1.38274C15.9738 1.26144 15.9999 1.13128 15.9999 0.999813C15.9999 0.868347 15.9738 0.738187 15.9231 0.616882C15.8724 0.495577 15.7982 0.385539 15.7047 0.293147C15.5163 0.107493 15.2625 0.00341797 14.998 0.00341797C14.7335 0.00341797 14.4797 0.107493 14.2913 0.293147L8.118 6.46648C8.10245 6.48261 8.08381 6.49544 8.06319 6.5042C8.04258 6.51297 8.0204 6.51748 7.998 6.51748C7.9756 6.51748 7.95342 6.51297 7.9328 6.5042C7.91219 6.49544 7.89355 6.48261 7.878 6.46648L1.70467 0.293147C1.51632 0.107493 1.26247 0.00341797 0.998001 0.00341797C0.733532 0.00341797 0.479686 0.107493 0.291334 0.293147C0.197808 0.385539 0.123552 0.495577 0.0728711 0.616882C0.0221905 0.738187 -0.00390625 0.868347 -0.00390625 0.999813C-0.00390625 1.13128 0.0221905 1.26144 0.0728711 1.38274C0.123552 1.50405 0.197808 1.61409 0.291334 1.70648L6.46467 7.88648C6.48061 7.90144 6.49332 7.91951 6.502 7.93958C6.51069 7.95965 6.51517 7.98128 6.51517 8.00315C6.51517 8.02501 6.51069 8.04664 6.502 8.06671C6.49332 8.08678 6.48061 8.10485 6.46467 8.11981L0.291334 14.2931C0.197808 14.3855 0.123552 14.4956 0.0728711 14.6169C0.0221905 14.7382 -0.00390625 14.8683 -0.00390625 14.9998C-0.00390625 15.1313 0.0221905 15.2614 0.0728711 15.3827C0.123552 15.504 0.197808 15.6141 0.291334 15.7065C0.479686 15.8921 0.733532 15.9962 0.998001 15.9962C1.26247 15.9962 1.51632 15.8921 1.70467 15.7065L7.878 9.53314C7.89355 9.51701 7.91219 9.50418 7.9328 9.49542C7.95342 9.48666 7.9756 9.48214 7.998 9.48214C8.0204 9.48214 8.04258 9.48666 8.06319 9.49542C8.08381 9.50418 8.10245 9.51701 8.118 9.53314L14.2913 15.7065C14.4797 15.8921 14.7335 15.9962 14.998 15.9962C15.2625 15.9962 15.5163 15.8921 15.7047 15.7065C15.7982 15.6141 15.8724 15.504 15.9231 15.3827C15.9738 15.2614 15.9999 15.1313 15.9999 14.9998C15.9999 14.8683 15.9738 14.7382 15.9231 14.6169C15.8724 14.4956 15.7982 14.3855 15.7047 14.2931L9.53133 8.11981Z'
                      fill='#151515'
                    />
                  </g>
                  <defs>
                    <clipPath id='clip0_1772_73836'>
                      <rect width='16' height='16' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>

            {formMode === 'confirmation' && (
              <Confirmation
                activatePurchaseDapForm={activatePurchaseDapForm}
                setLoading={setLoading}
                setOnMobileDialogMode={setOnMobileDialogMode}
              />
            )}

            {formMode === 'purchaseDap' && (
              <PurchaseDAP
                activateEditPaymentMethod={() => {
                  setFormMode('updatePaymentMethod');
                }}
              />
            )}

            {formMode === 'updatePaymentMethod' && (
              <UpdatePaymentMethod paymentMethodError={paymentMethodError} />
            )}

            {formMode === 'reservation' && (
              <div className='p-10 pb-0 flex items-center gap-8'>
                {/* <div className='md:hidden'>
                  <button
                    className={`rounded-full p-5 bg-[#f5f5f5] flex justify-center items-center cursor-pointer transition-opacity ${
                      formVisible
                        ? 'opacity-100 pointer-events-auto'
                        : 'opacity-0 pointer-events-none'
                    }`}
                    onClick={handleCloseForm}
                  >
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <g clipPath='url(#clip0_1761_31212)'>
                        <path
                          d='M3.00107 7.99988C3.00107 8.46655 3.20107 8.86655 3.53441 9.13322L11.0011 15.7332C11.5344 16.1999 12.3344 16.1332 12.7344 15.5999C13.1344 15.0665 13.1344 14.3332 12.6011 13.8665L6.06774 8.13322C6.00107 8.06655 6.00107 7.99989 6.06774 7.86655L12.6011 2.13322C13.1344 1.66655 13.1344 0.866554 12.6677 0.399887C12.2011 -0.0667795 11.4677 -0.133446 10.9344 0.266554L3.46774 6.79989C3.20108 7.13322 3.00107 7.53322 3.00107 7.99988Z'
                          fill='#151515'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_1761_31212'>
                          <rect
                            width='16'
                            height='16'
                            fill='white'
                            transform='matrix(-1 0 0 1 16 0)'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  </div> */}
                <div className='flex gap-5 justify-between items-center grow'>
                  <button
                    className={`inline-flex appearance-none cursor-pointer transition-colors bg-transparent text-sm font-bold font-wide text-neutral-900`}
                    onClick={() => {
                      if (step !== 1) setStep(1);
                    }}
                  >
                    Overview
                  </button>
                  <div
                    className={`w-full h-px flex-grow transition-colors  ${
                      step >= 2 ? 'bg-neutral-500' : 'bg-gray-200'
                    }`}
                  ></div>
                  <button
                    className={`inline-flex appearance-none cursor-pointer transition-colors bg-transparent text-sm font-bold font-wide ${
                      step >= 2 ? 'text-neutral-900' : 'text-stone-300'
                    }`}
                    onClick={() => {
                      if (step === 3) setStep(2);
                      if (step === 1 && checkUserDetails()) setStep(2);
                    }}
                  >
                    Payment
                  </button>
                  <div
                    className={`w-full h-px flex-grow transition-colors  ${
                      step >= 3 ? 'bg-neutral-500' : 'bg-gray-200'
                    }`}
                  ></div>
                  <button
                    className={`inline-flex appearance-none cursor-pointer transition-colors bg-transparent text-sm font-bold font-wide ${
                      step >= 3 ? 'text-neutral-900' : 'text-stone-300'
                    }`}
                    onClick={() => {
                      if (step === 2) setStep(3);
                      if (
                        step === 1 &&
                        checkUserDetails() &&
                        stripePaymentMethod !== null
                      )
                        setStep(3);
                    }}
                  >
                    Review
                  </button>
                </div>
              </div>
            )}

            {formMode === 'thankYou' && <ThankYou />}
            {formMode === 'thankYouDAP' && <ThankYouDAP />}
            {formMode === 'reservation' && (
              <React.Fragment>
                {step === 1 && <Overview formError={userDetailsError} />}
                {step === 2 && (
                  <Payment
                    hasPaymentMethod={stripePaymentMethod}
                    paymentMethodError={paymentMethodError}
                  />
                )}
                {step === 3 && <Summary />}
              </React.Fragment>
            )}
          </Box>

          {formMode === 'reservation' && (
            <div className='summary-container'>
              {step < 3 && (
                <div className='flex justify-between items-center gap-10'>
                  <div className='dueTodayContainer'>
                    <div className='flex flex-col'>
                      <div className='text-small'>Total</div>
                      <div className='text-xl md:text-2xl font-extrabold font-wide uppercase whitespace-nowrap'>
                        {formatCurrency(dueAmount)} USD
                      </div>
                    </div>
                  </div>

                  {step === 1 && (
                    <button
                      className={contButtonStyles}
                      onClick={() => stepForward()}
                      // disabled={!personalDetailsCompleted}
                    >
                      Continue
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 14 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='w-[14px] shrink-0'
                      >
                        <g clipPath='url(#clip0_2313_6277)'>
                          <path
                            d='M6.27524 0.701045L6.27521 0.701022C6.23952 0.74068 6.21212 0.787083 6.19462 0.8375C6.17713 0.887913 6.1699 0.941315 6.17337 0.994565C6.17683 1.04781 6.19091 1.09983 6.21478 1.14755L5.84213 1.33395L6.21478 1.14756C6.23865 1.19528 6.27183 1.23774 6.31236 1.27245L6.31675 1.27621L6.31672 1.27624L11.2505 5.62097C11.3374 5.69659 11.399 5.79709 11.4269 5.90889L11.4269 5.90891C11.4551 6.02172 11.4477 6.14051 11.4056 6.24892L11.402 6.2581L11.4019 6.25805C11.3603 6.35826 11.2908 6.44445 11.2017 6.50643C11.1126 6.56841 11.0076 6.60359 10.8992 6.60779L10.8911 6.60811L10.883 6.60811L0.814687 6.60811C0.707053 6.60811 0.603828 6.65087 0.52772 6.72697L0.235527 6.43478L0.527719 6.72697C0.451611 6.80308 0.408854 6.90631 0.408854 7.01394C0.408854 7.12157 0.451611 7.2248 0.52772 7.30091C0.603828 7.37702 0.707054 7.41977 0.814687 7.41977L10.883 7.41977L10.8911 7.41977L10.8992 7.42009L6.27524 0.701045ZM6.27524 0.701045L6.27875 0.697037M6.27524 0.701045L6.27875 0.697037M6.27875 0.697037C6.3139 0.656972 6.3566 0.624219 6.40441 0.60065C6.45222 0.577082 6.5042 0.563164 6.55739 0.559689C6.61058 0.556215 6.66393 0.563252 6.7144 0.580398C6.76487 0.597544 6.81146 0.624466 6.85153 0.659623L6.85157 0.659663L13.2674 6.28811C13.2676 6.28826 13.2678 6.2884 13.2679 6.28855C13.3698 6.37856 13.4515 6.48919 13.5074 6.61313C13.5635 6.73726 13.5924 6.87191 13.5924 7.00811C13.5924 7.14431 13.5635 7.27895 13.5074 7.40309C13.4515 7.52705 13.3698 7.6377 13.2679 7.72772C13.2677 7.72785 13.2676 7.72798 13.2674 7.72811L6.85157 13.3566L6.85156 13.3566C6.77063 13.4276 6.66482 13.4635 6.55739 13.4565C6.44997 13.4495 6.34973 13.4001 6.27873 13.3191C6.20773 13.2382 6.17178 13.1324 6.1788 13.025C6.18582 12.9176 6.23522 12.8173 6.31615 12.7463L6.31651 12.746L11.2504 8.40701C11.3373 8.33139 11.399 8.23085 11.4269 8.11899L11.4269 8.11898C11.4551 8.00616 11.4477 7.88737 11.4056 7.77896L11.4057 7.77892M6.27875 0.697037L11.4057 7.77892M11.4057 7.77892L11.4019 7.76983M11.4057 7.77892L11.4019 7.76983M11.4019 7.76983C11.3603 7.66962 11.2908 7.58343 11.2017 7.52145L11.4019 7.76983Z'
                            fill='white'
                            stroke='white'
                            strokeWidth='0.833333'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_2313_6277'>
                            <rect width='14' height='14' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  )}

                  {step === 2 && (
                    <button
                      className={contButtonStyles}
                      onClick={() => handleSubmitPaymentMethod()}
                    >
                      Continue
                      <svg
                        width='14'
                        height='14'
                        viewBox='0 0 14 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='w-[14px] shrink-0'
                      >
                        <g clipPath='url(#clip0_2313_6277)'>
                          <path
                            d='M6.27524 0.701045L6.27521 0.701022C6.23952 0.74068 6.21212 0.787083 6.19462 0.8375C6.17713 0.887913 6.1699 0.941315 6.17337 0.994565C6.17683 1.04781 6.19091 1.09983 6.21478 1.14755L5.84213 1.33395L6.21478 1.14756C6.23865 1.19528 6.27183 1.23774 6.31236 1.27245L6.31675 1.27621L6.31672 1.27624L11.2505 5.62097C11.3374 5.69659 11.399 5.79709 11.4269 5.90889L11.4269 5.90891C11.4551 6.02172 11.4477 6.14051 11.4056 6.24892L11.402 6.2581L11.4019 6.25805C11.3603 6.35826 11.2908 6.44445 11.2017 6.50643C11.1126 6.56841 11.0076 6.60359 10.8992 6.60779L10.8911 6.60811L10.883 6.60811L0.814687 6.60811C0.707053 6.60811 0.603828 6.65087 0.52772 6.72697L0.235527 6.43478L0.527719 6.72697C0.451611 6.80308 0.408854 6.90631 0.408854 7.01394C0.408854 7.12157 0.451611 7.2248 0.52772 7.30091C0.603828 7.37702 0.707054 7.41977 0.814687 7.41977L10.883 7.41977L10.8911 7.41977L10.8992 7.42009L6.27524 0.701045ZM6.27524 0.701045L6.27875 0.697037M6.27524 0.701045L6.27875 0.697037M6.27875 0.697037C6.3139 0.656972 6.3566 0.624219 6.40441 0.60065C6.45222 0.577082 6.5042 0.563164 6.55739 0.559689C6.61058 0.556215 6.66393 0.563252 6.7144 0.580398C6.76487 0.597544 6.81146 0.624466 6.85153 0.659623L6.85157 0.659663L13.2674 6.28811C13.2676 6.28826 13.2678 6.2884 13.2679 6.28855C13.3698 6.37856 13.4515 6.48919 13.5074 6.61313C13.5635 6.73726 13.5924 6.87191 13.5924 7.00811C13.5924 7.14431 13.5635 7.27895 13.5074 7.40309C13.4515 7.52705 13.3698 7.6377 13.2679 7.72772C13.2677 7.72785 13.2676 7.72798 13.2674 7.72811L6.85157 13.3566L6.85156 13.3566C6.77063 13.4276 6.66482 13.4635 6.55739 13.4565C6.44997 13.4495 6.34973 13.4001 6.27873 13.3191C6.20773 13.2382 6.17178 13.1324 6.1788 13.025C6.18582 12.9176 6.23522 12.8173 6.31615 12.7463L6.31651 12.746L11.2504 8.40701C11.3373 8.33139 11.399 8.23085 11.4269 8.11899L11.4269 8.11898C11.4551 8.00616 11.4477 7.88737 11.4056 7.77896L11.4057 7.77892M6.27875 0.697037L11.4057 7.77892M11.4057 7.77892L11.4019 7.76983M11.4057 7.77892L11.4019 7.76983M11.4019 7.76983C11.3603 7.66962 11.2908 7.58343 11.2017 7.52145L11.4019 7.76983Z'
                            fill='white'
                            stroke='white'
                            strokeWidth='0.833333'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_2313_6277'>
                            <rect width='14' height='14' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  )}
                </div>
              )}
              {step === 3 && (
                <div className='flex flex-col gap-6'>
                  {confirmPaymentError && (
                    <div>
                      <div className='text-red-500 text-sm font-bold leading-normal tracking-wide'>
                        {confirmPaymentError}{' '}
                        <button
                          // style like a link
                          className='text-red-500 text-sm font-bold leading-normal tracking-wide underline bg-transparent cursor-pointer'
                          onClick={() => {
                            setStep(2);
                            setConfirmPaymentError(null);
                            setStripePaymentMethod(null);
                          }}
                        >
                          Please try another payment method
                        </button>
                      </div>
                    </div>
                  )}

                  {reservationSubmitError && (
                    <div>
                      <div className='text-red-500 text-sm font-bold leading-normal tracking-wide'>
                        There was an error saving your reservation. You have not
                        been charged.
                      </div>
                    </div>
                  )}
                  <button
                    className={confirmButtonStyles}
                    onClick={handleFinish}
                  >
                    Confirm & Pay
                  </button>
                  <div className='text-small font-wide'>
                    By placing a refundable reservation, I agree to the{' '}
                    <a
                      href='https://aiomotors.com/reservation-agreement'
                      target='_blank'
                      rel='noreferrer'
                      className='underline'
                    >
                      Reservation Agreement
                    </a>{' '}
                    and have read and accept the{' '}
                    <a
                      href='https://aiomotors.com/terms-of-use'
                      target='_blank'
                      rel='noreferrer'
                      className='underline'
                    >
                      Terms of Use
                    </a>{' '}
                    and{' '}
                    <a
                      href='https://aiomotors.com/privacy-policy'
                      target='_blank'
                      rel='noreferrer'
                      className='underline'
                    >
                      Privacy Policy
                    </a>
                    .
                  </div>
                </div>
              )}
            </div>
          )}

          {(formMode === 'thankYou' || formMode === 'thankYouDAP') && (
            <div className='summary-container'>
              <button
                className={confirmButtonStyles}
                onClick={() => setFormMode('confirmation')}
              >
                Done
              </button>
            </div>
          )}

          {formMode === 'purchaseDap' && (
            <div className='summary-container'>
              <div className='flex flex-col gap-6'>
                {confirmPaymentError && (
                  <div>
                    <div className='text-red-500 text-sm font-bold leading-normal tracking-wide'>
                      {confirmPaymentError}{' '}
                      <button
                        // style like a link
                        className='text-red-500 text-sm font-bold leading-normal tracking-wide underline bg-transparent cursor-pointer'
                        onClick={() => {
                          setStep(2);
                          setConfirmPaymentError(null);
                          setStripePaymentMethod(null);
                        }}
                      >
                        Please try another payment method
                      </button>
                    </div>
                  </div>
                )}

                {reservationSubmitError && (
                  <div>
                    <div className='text-red-500 text-sm font-bold leading-normal tracking-wide'>
                      There was an error saving your reservation. You have not
                      been charged.
                    </div>
                  </div>
                )}
                <button
                  className={confirmButtonStyles}
                  onClick={handleDapPurchase}
                >
                  Confirm & Pay
                </button>

                <button
                  className={cancelButtonStyles}
                  onClick={() => setFormMode('confirmation')}
                >
                  Cancel
                </button>

                <div className='text-small font-wide'>
                  By purchasing the digital asset package (DAP), I have read and
                  accept the{' '}
                  <a
                    href='https://aiomotors.com/terms-of-use'
                    target='_blank'
                    rel='noreferrer'
                    className='underline'
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href='https://aiomotors.com/privacy-policy'
                    target='_blank'
                    rel='noreferrer'
                    className='underline'
                  >
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>
            </div>
          )}

          {formMode === 'updatePaymentMethod' && (
            <div className='summary-container'>
              <div className='flex gap-4'>
                <button
                  className={cancelButtonStyles}
                  onClick={() => setFormMode('purchaseDap')}
                >
                  Cancel
                </button>

                <button
                  className={confirmButtonStyles}
                  onClick={handleUpdatePaymentMethod}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </Slide>
    </Box>
  );
}

export default ReservationForm;
